/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-33 (lee 22/10) — LUẬT SỐ của Tổng quan bản 3, THUẦN (không React, không API) để `kiem-tk32` chạy và phá.
 *
 * 🔴 MỘT NGUỒN: ô Khẩn / Cảnh báo / Mất kết nối, vòng tròn "Tình trạng tủ", tab của khung Vấn đề và số trên chuông đều
 *   đếm trên CÙNG một danh sách dòng — `dungDong()` (lib/vande.ts) phạm vi hệ thống — qua CÙNG bộ lọc `LOC_TAB`.
 *   Không ô nào tự đếm lại `alarms` theo cách riêng.
 * ⛔ LUẬT SỐ 0: không biết thì nói "chưa biết" — `null`, không 0. Tủ không nối mà máy chủ chưa dựng hồ sơ mất kết nối
 *   (chưa tới ngưỡng / chưa nối lần nào) KHÔNG được vẽ là "Bình thường" ⇒ lớp riêng "Chưa rõ".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { LOC_TAB, type Dong } from './vande';
import type { TreeRoute } from './types';

export interface TomTatViec {
  khan: number;
  canh: number;
  mat: number;
  /** Cảnh báo mở trong 60 phút qua (theo `raisedAt`) */
  canhGioQua: number;
  /** Tủ có hồ sơ mất kết nối (nguồn TỦ), mới nhất trước: tên + lúc */
  matTu: { cabinetId: string; ten: string; luc: string | null }[];
  /** Hồ sơ mất kết nối của ĐÈN NEMA (tủ vẫn nối) — tính vào ô, không tính vào lớp tủ */
  matDen: number;
}

export function tomTatViec(dong: Dong[], bayGio: number = Date.now()): TomTatViec {
  const khan = dong.filter(LOC_TAB.khan);
  const canh = dong.filter(LOC_TAB.canh);
  const mat = dong.filter(LOC_TAB.mat);
  const canhGioQua = canh.filter((d) => d.luc && bayGio - new Date(d.luc).getTime() <= 3_600_000).length;
  const theoTu = new Map<string, { cabinetId: string; ten: string; luc: string | null }>();
  for (const d of mat) {
    if (d.nguon !== 'tu') continue;
    const id = d.cabinetId ?? d.id;
    if (!theoTu.has(id)) theoTu.set(id, { cabinetId: id, ten: d.alarm?.cabinet?.name ?? d.noi ?? 'Tủ', luc: d.luc });
  }
  const matTu = [...theoTu.values()].sort((a, b) => (b.luc ?? '').localeCompare(a.luc ?? ''));
  return { khan: khan.length, canh: canh.length, mat: mat.length, canhGioQua, matTu, matDen: mat.filter((d) => d.nguon !== 'tu').length };
}

export type LopTu = 'binh-thuong' | 'canh-bao' | 'mat-ket-noi' | 'chua-ro';
export const THU_TU_LOP: LopTu[] = ['binh-thuong', 'canh-bao', 'mat-ket-noi', 'chua-ro'];

/**
 * Lớp của TỪNG tủ, đọc từ CÙNG các dòng của khung Vấn đề:
 *   có hồ sơ mất kết nối ⇒ Mất kết nối · có hồ sơ khẩn/cảnh báo ⇒ Cảnh báo · đang nối, không hồ sơ ⇒ Bình thường ·
 *   KHÔNG nối mà chưa có hồ sơ mất kết nối ⇒ Chưa rõ (luật số 0 — không vẽ thành "Bình thường").
 */
export function phanLopTu(cay: TreeRoute[] | undefined, dong: Dong[]): { dem: Record<LopTu, number>; tong: number } {
  /* Chỉ hồ sơ mất kết nối của TỦ: đèn NEMA mất kết nối trong một tủ đang nối không làm tủ thành "Mất kết nối". */
  const coMat = new Set(dong.filter((d) => LOC_TAB.mat(d) && d.nguon === 'tu').map((d) => d.cabinetId));
  const coViec = new Set(dong.filter((d) => LOC_TAB.khan(d) || LOC_TAB.canh(d)).map((d) => d.cabinetId));
  const dem: Record<LopTu, number> = { 'binh-thuong': 0, 'canh-bao': 0, 'mat-ket-noi': 0, 'chua-ro': 0 };
  let tong = 0;
  for (const r of cay ?? []) for (const c of r.cabinets ?? []) {
    tong++;
    if (coMat.has(c.id)) dem['mat-ket-noi']++;
    else if (coViec.has(c.id)) dem['canh-bao']++;
    else if (c.status === 'ONLINE') dem['binh-thuong']++;
    else dem['chua-ro']++;
  }
  return { dem, tong };
}

/**
 * MỘT vật cho cả hàng ô số và vòng tròn: tổng/trực tuyến/số tuyến đọc từ cây, việc đọc từ dòng khung Vấn đề.
 *   Ô "Tổng số tủ" và số giữa vòng tròn là CÙNG `tong` ⇒ không thể lệch nhau.
 */
export interface SoTongQuan extends TomTatViec {
  tong: number; online: number; soTuyen: number; lop: Record<LopTu, number>;
}
export function soTongQuan(cay: TreeRoute[] | undefined, dong: Dong[], bayGio: number = Date.now()): SoTongQuan | null {
  if (!Array.isArray(cay)) return null;
  const { dem, tong } = phanLopTu(cay, dong);
  let online = 0;
  for (const r of cay) for (const c of r.cabinets ?? []) if (c.status === 'ONLINE') online++;
  return { ...tomTatViec(dong, bayGio), tong, online, soTuyen: cay.length, lop: dem };
}

/** Việc nặng nhất trên một tuyến — tô số "trực tuyến/tổng" ở cây (đỏ · vàng · xám · xanh). */
export type MauTuyen = 'khan' | 'canh' | 'mat' | 'tot';
export function mauTuyen(cabinetIds: string[], dong: Dong[]): MauTuyen {
  const ids = new Set(cabinetIds);
  const cua = dong.filter((d) => d.cabinetId && ids.has(d.cabinetId));
  if (cua.some(LOC_TAB.khan)) return 'khan';
  if (cua.some(LOC_TAB.canh)) return 'canh';
  if (cua.some(LOC_TAB.mat)) return 'mat';
  return 'tot';
}

/**
 * Cột điện năng theo ngày. CHỈ ngày có số thật (`kwh` hữu hạn) mới thành cột; không có ngày nào ⇒ `null` ⇒ ô ghi
 * "Chưa có số đo" — KHÔNG vẽ cột 0 (luật số 0). Hôm nay máy chủ CHƯA có tuyến đọc kWh theo ngày toàn hệ thống,
 * nên Tổng quan gọi hàm này với `undefined`.
 */
export function cotDienNang(ds: { ngay: string; kwh: number | null }[] | undefined): { ngay: string; kwh: number }[] | null {
  const cot = (ds ?? []).filter((d): d is { ngay: string; kwh: number } => typeof d.kwh === 'number' && Number.isFinite(d.kwh));
  return cot.length ? cot : null;
}
